import React, { useEffect } from 'react'
import Image from 'next/image';
import { useTranslation } from '@/hooks/useTranslation';
import { useGame } from '@/app/providers/GameContextProvider';
import { useGlobalPopup } from '@/app/providers/GlobalPopupProvider';
import TextFFXIV from '@/app/components/TextFFXIV';
import { MAP_TYPE_CONFIG } from '@/lib/utils/gameConfigUtils';
import TooltipFFXIV from '@/app/components/TooltipFFXIV';
import { useAudio, AUDIO } from '@/app/providers/AudioProvider';


const GameModeConfigMapTypes = () => {
    const { room, gameState } = useGame();
    const { showPopup } = useGlobalPopup();
    const { t } = useTranslation();
    const { play } = useAudio();

    const isGameMaster = room?.sessionId === gameState?.gameMasterId;
    const selectedMapTypes = Array.from(gameState?.selectedMapTypes || []);

    // Save selected map types to localStorage whenever they change (game master only)
    useEffect(() => {
        if (isGameMaster && typeof window !== 'undefined' && selectedMapTypes.length > 0) {
            localStorage.setItem('eorguessr_selected_maptypes', JSON.stringify(selectedMapTypes));
        }
    }, [selectedMapTypes, isGameMaster]);

    const mapTypes = MAP_TYPE_CONFIG.map(config => ({
        name: t(`game.config.mapTypes.${config.mapType}`),
        mapType: config.mapType,
        icon: config.icon
    }));

    if (!room || !gameState) return null;

    return (
        <div className={`flex gap-3 flex-col md:flex-row items-center ${!isGameMaster ? 'opacity-50' : ''}`}>
            <TextFFXIV text={t("lobby.mapTypes")} className="w-[75px]" />
            <div className="flex gap-2.5">
                {mapTypes.map(m => {
                    const isSelected = selectedMapTypes.includes(m.mapType);
                    return (
                        <div
                            key={m.mapType}
                            className={`relative group flex gap-2 items-center z-10 ${isGameMaster ? 'cursor-pointer' : 'cursor-not-allowed'} ${isSelected ? '' : 'opacity-60'}`}
                            onMouseEnter={() => isGameMaster && play(AUDIO.HOVER)}
                            onClick={() => {
                                if (!isGameMaster) {
                                    showPopup({
                                        type: 'error',
                                        message: t('lobby.onlyGameMasterCan'),
                                    });
                                    return;
                                }
                                play(AUDIO.CLICK);
                                room.send("toggleMapType", { mapType: m.mapType });
                            }}
                        >
                            <TooltipFFXIV
                                showInMobile={false}
                            >
                                {m.name}
                            </TooltipFFXIV>
                            <div className="relative w-8 h-8">
                                {isSelected && (
                                    <div className="left-[-2px] top-[-2px] rounded-md absolute w-[36px] h-[36px] z-40 border-2 border-[#CDCCA8] shadow-[inset_0px_0px_2px_2px_rgba(0,0,0,0.6),0px_0px_2px_2px_rgba(0,0,0,0.5)]"></div>
                                )}
                                <div className="relative w-8 h-8 overflow-hidden rounded shadow shadow-black/50">
                                    {!isSelected && (
                                        <>
                                            <div className="top-[-0px] h-1 w-full bg-gradient-to-b from-white/30 to-transparent absolute"></div>
                                            <div className="bottom-[-0px] h-[6px] w-full bg-gradient-to-t from-black/30 via-black/30 via-50% to-transparent absolute"></div>
                                        </>
                                    )}
                                    <Image
                                        src={m.icon}
                                        alt={m.name}
                                        width={32}
                                        height={32}
                                    />
                                    <div className="absolute top-0 left-0 w-full h-full rounded bg-white group-hover:opacity-10 opacity-0 duration-100"></div>
                                </div>
                            </div>
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

export default GameModeConfigMapTypes